import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { USER_TYPES } from '../constants/userTypes';
import { getBackendBaseUrl } from '../utils/apiUrls';
import { optionalAuthHeaders } from '../utils/billingApi';

const BACKEND_URL = getBackendBaseUrl();

const WAITLIST_STATUSES = [
    { value: 'waitlist', label: 'Waitlist' },
    { value: 'approved', label: 'Approved' },
    { value: 'accessed', label: 'Accessed' },
];

const EMPTY_CREATE_FORM = {
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    user_type: 'real_estate_agent',
    brokerage: '',
    state: '',
    transaction_volume_per_year: '',
    status: 'waitlist',
    referral_link_id: '',
    referral_email_send_id: '',
    referral_offered_free_transactions: '',
    referral_claimed_at: '',
    license_number: '',
    company_address: '',
    company_license_number: '',
    profile_completed: false,
    send_waitlist_email: false,
};

const inputStyle = { width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' };
const labelStyle = { display: 'block', fontSize: '0.9rem', marginBottom: '4px' };
const fieldStyle = { marginBottom: '12px' };
const sectionStyle = { marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' };

function DeveloperWaitlist() {
    const { user, isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
    const navigate = useNavigate();

    const [waitlist, setWaitlist] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedEmails, setSelectedEmails] = useState(new Set());
    const [actionLoading, setActionLoading] = useState(false);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [createForm, setCreateForm] = useState({ ...EMPTY_CREATE_FORM });
    const [pendingEmailRetryEntryId, setPendingEmailRetryEntryId] = useState(null);
    const [pendingEmailRetryKind, setPendingEmailRetryKind] = useState(null);
    const [emailRetryMessage, setEmailRetryMessage] = useState('');
    const createModalSessionRef = useRef(0);

    const createSendEmailLabel = useMemo(() => {
        if (createForm.status === 'approved' || createForm.status === 'accessed') {
            return 'Send welcome / access-granted email';
        }
        return 'Send waitlist confirmation email';
    }, [createForm.status]);

    const isKeyFloUser = useMemo(() => {
        if (!user?.email) return false;
        const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];
        return ALLOWED_USERS.includes(user.email);
    }, [user]);

    useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated) {
            loginWithRedirect();
            return;
        }
        if (!isKeyFloUser) {
            navigate('/');
        }
    }, [isLoading, isAuthenticated, isKeyFloUser, navigate, loginWithRedirect]);

    const fetchWaitlist = async () => {
        if (!isAuthenticated || !isKeyFloUser) return;
        try {
            setLoading(true);
            setError('');

            const headers = await optionalAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const response = await axios.get(`${BACKEND_URL}/waitlist/dev/list`, { headers });

            if (typeof response.data === 'string' && response.data.trim().startsWith('<')) {
                throw new Error('Received HTML response instead of JSON. Check API URL.');
            }

            if (response.data?.status !== 'success') {
                throw new Error(response.data?.error || 'Failed to fetch waitlist');
            }

            setWaitlist(response.data.leads || []);
        } catch (e) {
            console.error('Fetch error:', e);
            setError(e.message || 'Failed to load waitlist');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isLoading && isAuthenticated && isKeyFloUser) fetchWaitlist();
    }, [isLoading, isAuthenticated, isKeyFloUser]);

    const updateCreateField = (field, value) => {
        setCreateForm((prev) => ({ ...prev, [field]: value }));
    };

    const resetCreateForm = () => {
        setCreateForm({ ...EMPTY_CREATE_FORM });
        setPendingEmailRetryEntryId(null);
        setPendingEmailRetryKind(null);
        setEmailRetryMessage('');
        setCreateLoading(false);
    };

    const closeCreateModal = () => {
        createModalSessionRef.current += 1;
        setShowCreateModal(false);
        resetCreateForm();
    };

    const openCreateModal = () => {
        createModalSessionRef.current += 1;
        resetCreateForm();
        setShowCreateModal(true);
    };

    const isCreateModalSessionActive = (sessionId) => sessionId === createModalSessionRef.current;

    const appendRawIntegerField = (payload, field, rawValue) => {
        const trimmed = String(rawValue).trim();
        if (trimmed !== '') {
            payload[field] = trimmed;
        }
    };

    const toUtcIsoFromDatetimeLocal = (value) => {
        const trimmed = String(value).trim();
        if (!trimmed) return '';
        const parsed = new Date(trimmed);
        if (Number.isNaN(parsed.getTime())) {
            return trimmed;
        }
        return parsed.toISOString();
    };

    const buildCreatePayload = () => {
        const payload = {
            email: createForm.email.trim(),
            first_name: createForm.first_name.trim() || undefined,
            last_name: createForm.last_name.trim() || undefined,
            phone_number: createForm.phone_number.trim() || undefined,
            user_type: createForm.user_type || undefined,
            brokerage: createForm.brokerage.trim() || undefined,
            state: createForm.state.trim() || undefined,
            status: createForm.status,
            license_number: createForm.license_number.trim() || undefined,
            company_address: createForm.company_address.trim() || undefined,
            company_license_number: createForm.company_license_number.trim() || undefined,
            profile_completed: createForm.profile_completed,
            send_waitlist_email: createForm.send_waitlist_email,
        };

        appendRawIntegerField(payload, 'transaction_volume_per_year', createForm.transaction_volume_per_year);
        appendRawIntegerField(payload, 'referral_link_id', createForm.referral_link_id);
        appendRawIntegerField(payload, 'referral_email_send_id', createForm.referral_email_send_id);
        appendRawIntegerField(payload, 'referral_offered_free_transactions', createForm.referral_offered_free_transactions);
        const referralClaimedAtUtc = toUtcIsoFromDatetimeLocal(createForm.referral_claimed_at);
        if (referralClaimedAtUtc) {
            payload.referral_claimed_at = referralClaimedAtUtc;
        }

        return payload;
    };

    const handleCreateEntry = async () => {
        if (!createForm.email.trim()) {
            alert('Email is required');
            return;
        }
        const stateCode = createForm.state.trim().toUpperCase();
        if (!stateCode || stateCode.length !== 2) {
            alert('Licensed state is required (2-letter US state code, e.g. OK)');
            return;
        }

        const sessionId = createModalSessionRef.current;
        try {
            setCreateLoading(true);
            const headers = await optionalAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const response = await axios.post(
                `${BACKEND_URL}/waitlist/dev/create`,
                buildCreatePayload(),
                { headers },
            );

            const createdStatus = response.data?.status;
            if (!isCreateModalSessionActive(sessionId)) {
                if (createdStatus === 'success' || createdStatus === 'partial_success') {
                    fetchWaitlist();
                }
                return;
            }

            if (createdStatus === 'partial_success') {
                fetchWaitlist();
                const entryId = response.data.entry?.id ?? null;
                if (entryId) {
                    setPendingEmailRetryEntryId(entryId);
                    if (createForm.status === 'waitlist') {
                        setPendingEmailRetryKind('confirmation');
                    } else if (createForm.status === 'approved' || createForm.status === 'accessed') {
                        setPendingEmailRetryKind('welcome');
                    } else {
                        setPendingEmailRetryKind(null);
                        setPendingEmailRetryEntryId(null);
                    }
                }
                setEmailRetryMessage(response.data.message || 'Waitlist entry created, but email failed to send.');
                return;
            }

            if (createdStatus === 'success') {
                closeCreateModal();
                fetchWaitlist();
                alert('Waitlist entry created successfully');
            } else {
                throw new Error(response.data?.message || 'Failed to create waitlist entry');
            }
        } catch (e) {
            if (!isCreateModalSessionActive(sessionId)) {
                return;
            }
            const apiErrors = e.response?.data?.errors;
            const message = apiErrors?.length
                ? apiErrors.join('\n')
                : (e.response?.data?.message || e.message || 'Failed to create waitlist entry');
            alert(message);
        } finally {
            if (isCreateModalSessionActive(sessionId)) {
                setCreateLoading(false);
            }
        }
    };

    const handleRetryConfirmationEmail = async () => {
        if (!pendingEmailRetryEntryId || !pendingEmailRetryKind) return;

        const sessionId = createModalSessionRef.current;
        const retryPath = pendingEmailRetryKind === 'welcome'
            ? `${BACKEND_URL}/waitlist/dev/${pendingEmailRetryEntryId}/send-welcome-email`
            : `${BACKEND_URL}/waitlist/dev/${pendingEmailRetryEntryId}/send-confirmation-email`;
        const successMessage = pendingEmailRetryKind === 'welcome'
            ? 'Welcome email sent successfully'
            : 'Waitlist confirmation email sent successfully';
        const failureMessage = pendingEmailRetryKind === 'welcome'
            ? 'Failed to send welcome email'
            : 'Failed to send waitlist confirmation email';

        try {
            setCreateLoading(true);
            const headers = await optionalAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const response = await axios.post(retryPath, {}, { headers });

            if (!isCreateModalSessionActive(sessionId)) {
                return;
            }

            if (response.data?.status === 'success' && response.data?.email_sent) {
                closeCreateModal();
                fetchWaitlist();
                alert(successMessage);
            } else {
                throw new Error(response.data?.message || failureMessage);
            }
        } catch (e) {
            if (!isCreateModalSessionActive(sessionId)) {
                return;
            }
            setEmailRetryMessage(
                e.response?.data?.message || e.message || failureMessage,
            );
        } finally {
            if (isCreateModalSessionActive(sessionId)) {
                setCreateLoading(false);
            }
        }
    };

    const handleGrantAccess = async () => {
        if (selectedEmails.size === 0) return;
        if (!window.confirm(`Are you sure you want to grant access to ${selectedEmails.size} users? This will send them a welcome email.`)) return;

        try {
            setActionLoading(true);
            const headers = await optionalAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const response = await axios.post(`${BACKEND_URL}/waitlist/grant-access`, {
                emails: Array.from(selectedEmails),
            }, { headers });

            if (response.data?.status === 'success') {
                alert(`Successfully granted access to ${response.data.granted_count} users.`);
                setSelectedEmails(new Set());
                fetchWaitlist();
            } else {
                alert('Failed to grant access. Check console for details.');
            }
        } catch (e) {
            console.error(e);
            alert('Error granting access: ' + e.message);
        } finally {
            setActionLoading(false);
        }
    };

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [entryToDelete, setEntryToDelete] = useState(null);

    const handleDeleteClick = (entryId) => {
        setEntryToDelete(entryId);
        setShowDeleteModal(true);
    };

    const confirmDeleteEntry = async () => {
        if (!entryToDelete) return;

        try {
            setLoading(true);
            const headers = await optionalAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const response = await axios.delete(`${BACKEND_URL}/waitlist/${entryToDelete}`, { headers });

            if (response.data?.status === 'success') {
                alert('Waitlist entry deleted successfully');
                fetchWaitlist();
            } else {
                throw new Error(response.data?.message || 'Failed to delete entry');
            }
        } catch (e) {
            alert(e.response?.data?.message || e.message || 'Failed to delete entry');
        } finally {
            setLoading(false);
            setShowDeleteModal(false);
            setEntryToDelete(null);
        }
    };

    const toggleSelection = (email) => {
        const newSet = new Set(selectedEmails);
        if (newSet.has(email)) {
            newSet.delete(email);
        } else {
            newSet.add(email);
        }
        setSelectedEmails(newSet);
    };

    const toggleAll = () => {
        if (selectedEmails.size === waitlist.length) {
            setSelectedEmails(new Set());
        } else {
            setSelectedEmails(new Set(waitlist.map((w) => w.email)));
        }
    };

    if (isLoading) return <LoadingSpinner />;
    if (!isAuthenticated) return <LoadingSpinner />;

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2>Developer • Waitlist Management</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                        onClick={openCreateModal}
                        style={{
                            backgroundColor: '#10b981',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        + Waitlist Entry
                    </button>
                    <button
                        onClick={handleGrantAccess}
                        disabled={selectedEmails.size === 0 || actionLoading}
                        style={{
                            backgroundColor: selectedEmails.size > 0 ? '#10b981' : '#ccc',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: selectedEmails.size > 0 ? 'pointer' : 'not-allowed',
                        }}
                    >
                        {actionLoading ? 'Processing...' : `Grant Access (${selectedEmails.size})`}
                    </button>
                    <button className="back-button" onClick={() => navigate('/')}>← Back</button>
                    <DeveloperNavigation />
                </div>
            </div>

            {loading && <LoadingSpinner />}
            {error && <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>}

            <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '4px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
                    <thead>
                        <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                            <th style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                                <input
                                    type="checkbox"
                                    checked={waitlist.length > 0 && selectedEmails.size === waitlist.length}
                                    onChange={toggleAll}
                                />
                            </th>
                            <th style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>Actions</th>
                            <th style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>ID</th>
                            <th style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>First Name</th>
                            <th style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>Last Name</th>
                            <th style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>Email</th>
                            <th style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>Phone</th>
                            <th style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>Type</th>
                            <th style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>Brokerage</th>
                            <th style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>State</th>
                            <th style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>Vol/Yr</th>
                            <th style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                            <th style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>Created At</th>
                            <th style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>Updated At</th>
                        </tr>
                    </thead>
                    <tbody>
                        {waitlist.map((lead) => (
                            <tr key={lead.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '12px' }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedEmails.has(lead.email)}
                                        onChange={() => toggleSelection(lead.email)}
                                    />
                                </td>
                                <td style={{ padding: '12px' }}>
                                    <button
                                        onClick={() => handleDeleteClick(lead.id)}
                                        style={{
                                            backgroundColor: '#ef4444',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '4px 8px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Delete
                                    </button>
                                </td>
                                <td style={{ padding: '12px' }}>{lead.id}</td>
                                <td style={{ padding: '12px' }}>{lead.first_name}</td>
                                <td style={{ padding: '12px' }}>{lead.last_name}</td>
                                <td style={{ padding: '12px' }}>{lead.email}</td>
                                <td style={{ padding: '12px' }}>{lead.phone_number}</td>
                                <td style={{ padding: '12px' }}>{lead.user_type}</td>
                                <td style={{ padding: '12px' }}>{lead.brokerage}</td>
                                <td style={{ padding: '12px' }}>{lead.state}</td>
                                <td style={{ padding: '12px' }}>{lead.transaction_volume}</td>
                                <td style={{ padding: '12px' }}>
                                    <span style={{
                                        padding: '4px 8px',
                                        borderRadius: '12px',
                                        fontSize: '12px',
                                        backgroundColor: lead.status === 'approved' ? '#ecfdf5' : '#fffbeb',
                                        color: lead.status === 'approved' ? '#047857' : '#b45309',
                                    }}>
                                        {lead.status}
                                    </span>
                                </td>
                                <td style={{ padding: '12px' }}>{lead.created_at ? new Date(lead.created_at).toLocaleString() : ''}</td>
                                <td style={{ padding: '12px' }}>{lead.updated_at ? new Date(lead.updated_at).toLocaleString() : ''}</td>
                            </tr>
                        ))}
                        {waitlist.length === 0 && !loading && (
                            <tr>
                                <td colSpan="14" style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
                                    No waitlist entries found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {showCreateModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                }}>
                    <div style={{
                        background: 'white', padding: '24px', borderRadius: '8px', width: '560px', maxWidth: '95%', maxHeight: '90vh', overflowY: 'auto',
                    }}>
                        <h3 style={{ marginTop: 0 }}>Create Waitlist Entry</h3>

                        <div style={sectionStyle}>
                            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem' }}>Contact</h4>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Email *</label>
                                <input style={inputStyle} value={createForm.email} onChange={(e) => updateCreateField('email', e.target.value)} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div style={fieldStyle}>
                                    <label style={labelStyle}>First Name</label>
                                    <input style={inputStyle} value={createForm.first_name} onChange={(e) => updateCreateField('first_name', e.target.value)} />
                                </div>
                                <div style={fieldStyle}>
                                    <label style={labelStyle}>Last Name</label>
                                    <input style={inputStyle} value={createForm.last_name} onChange={(e) => updateCreateField('last_name', e.target.value)} />
                                </div>
                            </div>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Phone Number</label>
                                <input style={inputStyle} value={createForm.phone_number} onChange={(e) => updateCreateField('phone_number', e.target.value)} placeholder="e.g. 5551234567" />
                            </div>
                        </div>

                        <div style={sectionStyle}>
                            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem' }}>Professional</h4>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>User Type</label>
                                <select style={inputStyle} value={createForm.user_type} onChange={(e) => updateCreateField('user_type', e.target.value)}>
                                    {USER_TYPES.map(({ value, label }) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Brokerage</label>
                                <input style={inputStyle} value={createForm.brokerage} onChange={(e) => updateCreateField('brokerage', e.target.value)} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div style={fieldStyle}>
                                    <label style={labelStyle}>Licensed State *</label>
                                    <input
                                        style={inputStyle}
                                        value={createForm.state}
                                        onChange={(e) => updateCreateField('state', e.target.value.toUpperCase())}
                                        placeholder="e.g. OK"
                                        maxLength={2}
                                        autoComplete="off"
                                    />
                                </div>
                                <div style={fieldStyle}>
                                    <label style={labelStyle}>Transactions / Year</label>
                                    <input style={inputStyle} type="number" min="0" max="100" value={createForm.transaction_volume_per_year} onChange={(e) => updateCreateField('transaction_volume_per_year', e.target.value)} />
                                </div>
                            </div>
                        </div>

                        <div style={sectionStyle}>
                            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem' }}>Status</h4>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Status</label>
                                <select style={inputStyle} value={createForm.status} onChange={(e) => updateCreateField('status', e.target.value)}>
                                    {WAITLIST_STATUSES.map(({ value, label }) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div style={sectionStyle}>
                            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem' }}>Referral</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div style={fieldStyle}>
                                    <label style={labelStyle}>Referral Link ID</label>
                                    <input style={inputStyle} type="number" value={createForm.referral_link_id} onChange={(e) => updateCreateField('referral_link_id', e.target.value)} />
                                </div>
                                <div style={fieldStyle}>
                                    <label style={labelStyle}>Referral Email Send ID</label>
                                    <input style={inputStyle} type="number" value={createForm.referral_email_send_id} onChange={(e) => updateCreateField('referral_email_send_id', e.target.value)} />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div style={fieldStyle}>
                                    <label style={labelStyle}>Offered Free Transactions</label>
                                    <input style={inputStyle} type="number" value={createForm.referral_offered_free_transactions} onChange={(e) => updateCreateField('referral_offered_free_transactions', e.target.value)} />
                                </div>
                                <div style={fieldStyle}>
                                    <label style={labelStyle}>Referral Claimed At (local time)</label>
                                    <input style={inputStyle} type="datetime-local" value={createForm.referral_claimed_at} onChange={(e) => updateCreateField('referral_claimed_at', e.target.value)} />
                                </div>
                            </div>
                        </div>

                        <div style={sectionStyle}>
                            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem' }}>Profile</h4>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>License Number</label>
                                <input style={inputStyle} value={createForm.license_number} onChange={(e) => updateCreateField('license_number', e.target.value)} />
                            </div>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Company Address</label>
                                <textarea style={{ ...inputStyle, minHeight: '64px' }} value={createForm.company_address} onChange={(e) => updateCreateField('company_address', e.target.value)} />
                            </div>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Company License Number</label>
                                <input style={inputStyle} value={createForm.company_license_number} onChange={(e) => updateCreateField('company_license_number', e.target.value)} />
                            </div>
                            <div style={{ ...fieldStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    id="createProfileCompleted"
                                    checked={createForm.profile_completed}
                                    onChange={(e) => updateCreateField('profile_completed', e.target.checked)}
                                />
                                <label htmlFor="createProfileCompleted" style={{ fontSize: '0.9rem' }}>Profile Completed</label>
                            </div>
                        </div>

                        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="checkbox"
                                id="createSendWaitlistEmail"
                                checked={createForm.send_waitlist_email}
                                onChange={(e) => updateCreateField('send_waitlist_email', e.target.checked)}
                                disabled={Boolean(pendingEmailRetryEntryId)}
                            />
                            <label htmlFor="createSendWaitlistEmail" style={{ fontSize: '0.9rem' }}>{createSendEmailLabel}</label>
                        </div>

                        {emailRetryMessage && (
                            <div style={{
                                marginBottom: '16px',
                                padding: '12px',
                                borderRadius: '4px',
                                background: '#fffbeb',
                                color: '#92400e',
                                border: '1px solid #fcd34d',
                                fontSize: '0.9rem',
                            }}>
                                {emailRetryMessage}
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <button
                                onClick={closeCreateModal}
                                style={{ background: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151', padding: '8px 16px', borderRadius: '4px' }}
                            >
                                Cancel
                            </button>
                            {pendingEmailRetryEntryId && pendingEmailRetryKind ? (
                                <button
                                    onClick={handleRetryConfirmationEmail}
                                    disabled={createLoading}
                                    style={{ background: '#f59e0b', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '4px' }}
                                >
                                    {createLoading
                                        ? 'Sending...'
                                        : (pendingEmailRetryKind === 'welcome'
                                            ? 'Retry welcome email'
                                            : 'Retry confirmation email')}
                                </button>
                            ) : (
                                <button
                                    onClick={handleCreateEntry}
                                    disabled={createLoading}
                                    style={{ background: '#10b981', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '4px' }}
                                >
                                    {createLoading ? 'Creating...' : 'Create'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                }}>
                    <div style={{ background: 'white', padding: '24px', borderRadius: '8px', width: '400px', maxWidth: '90%' }}>
                        <h3 style={{ marginTop: 0, color: '#ef4444' }}>Confirm Delete</h3>
                        <p>Are you sure you want to delete this waitlist entry? This action cannot be undone.</p>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
                            <button
                                onClick={() => { setShowDeleteModal(false); setEntryToDelete(null); }}
                                style={{ background: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151', padding: '8px 16px', borderRadius: '4px' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteEntry}
                                disabled={loading}
                                style={{ background: '#ef4444', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '4px' }}
                            >
                                {loading ? 'Deleting...' : 'Delete Entry'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DeveloperWaitlist;
